import { motion } from "framer-motion";
import { HardHat, ShieldCheck, Hand, Package, BatteryCharging, Plug, Wrench, ArrowRight } from "lucide-react";
import { imageUrl } from "../lib/api";

const CATEGORIES = [
  { icon: HardHat, title: "Helmets", desc: "Adult & kids motocross helmets, sized on enquiry." },
  { icon: ShieldCheck, title: "Protective Gear", desc: "Knee/elbow pads, chest protectors, body armour." },
  { icon: Hand, title: "Gloves", desc: "Grip-first riding gloves for adults and kids." },
  { icon: Package, title: "Bike Covers & Bags", desc: "Covers and padded carry bags for transport." },
  { icon: BatteryCharging, title: "Spare Batteries", desc: "Extend the ride — model-matched spares." },
  { icon: Plug, title: "Chargers", desc: "Standard and fast chargers per model." },
  { icon: Wrench, title: "Maintenance Kits", desc: "Tools, spares and consumables to keep you rolling." },
];

export const AccessoriesSection = () => {
  const go = (e) => {
    e.preventDefault();
    window.__lenis?.scrollTo("#contact", { offset: -70 });
  };

  return (
    <section id="accessories" data-testid="accessories-section" className="relative scroll-mt-24 border-t border-border py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[380px_1fr]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mono-label flex items-center gap-3">
              <span className="inline-block h-0.5 w-10 bg-brass" />
              Gear Up
            </p>
            <h2 className="mt-3 font-serif text-4xl font-black uppercase tracking-tight text-bone sm:text-5xl">
              Accessories &amp; <span className="text-brass">protection</span>
            </h2>
            <p className="mt-3 text-base leading-relaxed text-sand">
              Everything matched to the bikes we sell — adult and kids sizes. Add any
              of it to your bike enquiry and we bundle it in.
            </p>
            <div className="clip-corner mt-6 overflow-hidden">
              <img
                src={imageUrl("/api/images/accessories-sxo.jpg")}
                alt="Young riders in full protective gear — helmets, pads, gloves and boots"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <a
              href="#contact"
              data-testid="accessories-enquire-cta"
              onClick={go}
              className="clip-corner-sm mt-6 inline-flex items-center gap-2 bg-brass px-6 py-3.5 font-mono text-[10px] font-bold tracking-[0.2em] text-white uppercase transition-colors hover:bg-brass-bright"
            >
              Ask About Gear
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </motion.div>

          <div className="grid gap-px border border-border bg-border sm:grid-cols-2">
            {CATEGORIES.map((c, i) => (
              <motion.div
                key={c.title}
                data-testid={`accessory-category-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-forest-deep p-6 transition-colors duration-300 hover:bg-forest-emerald"
              >
                <c.icon className="h-6 w-6 text-cyanx transition-colors group-hover:text-brass" strokeWidth={1.75} />
                <h3 className="mt-4 font-serif text-xl font-bold uppercase text-bone">{c.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-sand">{c.desc}</p>
              </motion.div>
            ))}
            <motion.a
              href="#contact"
              data-testid="accessories-grid-enquire"
              onClick={go}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group flex items-center justify-between bg-volt p-6 text-forest-deep transition-colors hover:bg-brass hover:text-white"
            >
              <span className="font-serif text-xl font-black uppercase">Bundle it with your bike</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};
